import { useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { TableContainer, Table, TableHead, TableCell, TableRow, TableBody } from '@material-ui/core';
import { FaFilter } from 'react-icons/fa6'

function isPrimitive(x) {
    return x == null || (typeof x !== 'object');
}

function buildRows(data) {
    const rows = [];
    const walk = (prefix, val) => {
        if (isPrimitive(val)) {
            rows.push({ variable: prefix, fields: { value: val } });
            return;
        }
        if (Array.isArray(val)) {
            val.forEach((item, i) => walk(`${prefix}[${i}]`, item));
            return;
        }
        if (val && typeof val === 'object') {
            rows.push({ variable: prefix, fields: val });
            for (const [k, v] of Object.entries(val)) {
                if (typeof v === 'object') walk(prefix ? `${prefix}.${k}` : k, v);
            }
            return;
        }
    };
    for (const [k, v] of Object.entries(data || {})) walk(k, v);
    return rows;
}

// function buildColumns(rows) {
//     const present = new Set();
//     rows.forEach(r => Object.keys(r.fields || {}).forEach(c => present.add(c)));
//     return ['variable', ...Array.from(present)];
// }

function buildColumns(rows) {
    const present = new Set();
    rows.forEach(r => Object.keys(r.fields || {}).forEach(c => present.add(c)));
    const ordered = ['variable'];
    const presentArr = Array.from(present);
    if (presentArr.includes('value')) ordered.push('value');
    if (presentArr.includes('uom')) ordered.push('uom');
    const rest = presentArr.filter(c => c !== 'value' && c !== 'uom');
    return [...ordered, ...rest];
}

function defaultFmt(v) {
    if (v == null || Number.isNaN(v)) return '-';
    if (typeof v === 'number') {
        const a = Math.abs(v);
        if (a >= 100) return v.toFixed(1);
        if (a >= 1) return v.toFixed(2);
        return v.toFixed(3);
    }
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    return String(v);
}

export default function OptimizationPipelineConfigurableTable({
    data,
    title = 'Filter',
    searchable = true,
    initialQuery = '',
    emptyText = 'No data available.',
    striped = true,
    rowClassName,
    valueFormat
}) {
    const [q, setQ] = useState(initialQuery);
    const [sortState, setSortState] = useState(null); // { key, dir }

    const rowsAll = useMemo(() => buildRows(data), [data]);
    const columns = useMemo(() => buildColumns(rowsAll), [rowsAll]);

    const getCellRawValue = (row, colKey) => {
        if (colKey === 'variable') return row.variable;
        return row?.fields?.[colKey];
    };

    const renderCellValue = (row, colKey) => {
        const raw = getCellRawValue(row, colKey);
        const fmt = typeof valueFormat === 'function' ? valueFormat : defaultFmt;
        return fmt(raw, row, colKey);
    };

    const filtered = useMemo(() => {
        let result = [...rowsAll];
        if (q.trim() && searchable) {
            const needle = q.trim().toLowerCase();
            result = result.filter(row => {
                if (String(row.variable ?? '').toLowerCase().includes(needle)) return true;
                return Object.values(row.fields || {}).some(v => String(v ?? '').toLowerCase().includes(needle));
            });
        }
        return result;
    }, [rowsAll, q, searchable]);

    const toggleSort = (key) => {
        setSortState(prev => {
            if (!prev || prev.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            return null;
        });
    };

    const sorted = useMemo(() => {
        if (!sortState) return filtered;
        const { key, dir } = sortState;
        const mult = dir === 'desc' ? -1 : 1;
        const copy = [...filtered];
        copy.sort((a, b) => {
            const va = getCellRawValue(a, key);
            const vb = getCellRawValue(b, key);
            if (va == null && vb == null) return 0;
            if (va == null) return -1 * mult;
            if (vb == null) return 1 * mult;
            const na = Number(va), nb = Number(vb);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * mult;
            return String(va).localeCompare(String(vb)) * mult;
        });
        return copy;
    }, [filtered, sortState]);

    const headerCellClass = 'text-xs font-semibold whitespace-nowrap select-none cursor-pointer';
    const bodyCellBase = 'whitespace-nowrap text-xs tabular-nums';

    return (
        <TableContainer component={Paper}>
            <div className='w-full flex itemscenter justify-between bg-black text-white p-6'>
                <div className='font-medium flex gap-12 items-center ml-12'>
                    <FaFilter className='opacity-80' />
                    <span>{title}</span>
                </div>
                {searchable && (
                    <div className='flex items-center gap-16'>
                        <input
                            className='border border-white border-opacity-20 bg-transparent rounded px-6 py-3 text-xs outline-none focus:ring-2 focus:ring-white ring-opacity-20 placeholder-white'
                            placeholder='Search...'
                            value={q}
                            onChange={e => setQ(e.target.value)}
                        />
                    </div>
                )}
            </div>

            <Table className='w-full shadow-2xl'>
                <TableHead>
                    <TableRow>
                        {columns.map(col => {
                            const isSorted = sortState?.key === col;
                            const dir = isSorted ? sortState?.dir : null;
                            const align = col === 'variable' ? 'left' : 'right';
                            return (
                                <TableCell key={col} align={align} className='text-xs' onClick={() => toggleSort(col)}>
                                    <span className={`${headerCellClass} inline-flex items-center gap-8 capitalize`}>
                                        {col}
                                        <span className='text-10 opacity-60'>
                                            {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
                                        </span>
                                    </span>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {sorted.length > 0 ? (
                        sorted.map((r, idx) => {
                            const stripedClass = striped && (idx % 2 === 1) ? 'bg-gray-100' : '';
                            const extra = typeof rowClassName === 'function' ? (rowClassName(r, idx) || '') : '';
                            return (
                                <TableRow key={r.variable + idx} className={`${stripedClass} ${extra}`}>
                                    {columns.map(col => (
                                        <TableCell key={col} align={col === 'variable' ? 'left' : 'right'} className={bodyCellBase}>
                                            {renderCellValue(r, col)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} align='center' className='py-16 opacity-60 text-xs'>
                                {emptyText}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}